import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Switch from '@material-ui/core/Switch'
import VisibilityIcon from '@material-ui/icons/Visibility'
import moment from 'moment'

import { ERRORS, STRINGS } from 'consts'
import { UserContext } from 'contexts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { PageHeader } from 'components/PageHeader'
import { api, createDateSort, createStringSort, ConfigUtils } from 'utils'
import { canRespondToReviews } from 'utils/roles'

import { getStores } from '../Stores/api'
import { GetStoresResponse, Store } from '../Stores/types'
import { DefaultTable } from '../components'
import { getReviews, downloadReviewsCsv, updateReviewResponded, ReviewsFilters } from './api'
import { GetReviewsResponse, ReviewRow } from './types'
import { ReviewDetailDialog } from './components/ReviewDetailDialog'
import { ReviewIndicatorsPanel } from './indicators'

const COMMENT_PREVIEW_LENGTH = 80

const truncateComment = (text?: string) => {
  if (!text) return '—'
  if (text.length <= COMMENT_PREVIEW_LENGTH) return text
  return `${text.slice(0, COMMENT_PREVIEW_LENGTH)}…`
}

const reviewDate = (row: ReviewRow) =>
  row.source === 'google' && row.googleReviewTime ? row.googleReviewTime : row.createdAt

const reviewReviewerLabel = (row: ReviewRow) => {
  if (row.user) return `${row.user.fullname || ''} (${row.user.email || ''})`.trim()
  if ((row.source === 'google' || row.source === 'qr') && row.reviewerName) {
    if (row.source === 'qr' && row.qrEmail) {
      return `${row.reviewerName} (${row.qrEmail})`
    }
    return row.reviewerName
  }
  return ''
}

const getStoresGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []

const buildDefaultFilters = (): ReviewsFilters => ({
  storeId: '',
  minStars: '',
  maxStars: '',
  source: 'all',
  responded: 'all',
  dateFrom: moment()
    .subtract(30, 'days')
    .format('YYYY-MM-DD'),
  dateTo: moment().format('YYYY-MM-DD'),
})

const Reviews: React.FC = () => {
  const { admin } = useContext(UserContext)
  const canRespond = canRespondToReviews(admin)
  const [initialFilters] = useState(buildDefaultFilters)
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom ?? '')
  const [dateTo, setDateTo] = useState(initialFilters.dateTo ?? '')
  const [filterStoreId, setFilterStoreId] = useState<number | ''>('')
  const [filterMinStars, setFilterMinStars] = useState<number | ''>('')
  const [filterMaxStars, setFilterMaxStars] = useState<number | ''>('')
  const [source, setSource] = useState('all')
  const [respondedFilter, setRespondedFilter] = useState<'all' | 'true' | 'false'>('all')
  const [selectedReview, setSelectedReview] = useState<ReviewRow | null>(null)
  const [togglingReviewId, setTogglingReviewId] = useState<number | null>(null)
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<ReviewsFilters>(initialFilters)
  const [refreshTrigger, setRefreshTrigger] = useState(1)
  const [reviews, setReviews] = useState<ReviewRow[]>([])

  const currentFilters = useMemo(
    () => ({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      storeId: filterStoreId,
      minStars: filterMinStars,
      maxStars: filterMaxStars,
      source,
      responded: respondedFilter,
    }),
    [dateFrom, dateTo, filterStoreId, filterMinStars, filterMaxStars, source, respondedFilter],
  )

  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(getStores, getStoresGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [fetchReviews, isLoading] = useApiCall<ReviewsFilters, GetReviewsResponse>(getReviews)

  const loadReviews = useCallback(
    async (filters: ReviewsFilters) => {
      try {
        const response = await fetchReviews(filters)
        setReviews(response.reviews ?? [])
      } catch {
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      }
    },
    [fetchReviews],
  )

  const handleRefresh = useCallback(async () => {
    setAppliedFilters(currentFilters)
    setRefreshTrigger(prev => prev + 1)
    await loadReviews(currentFilters)
  }, [currentFilters, loadReviews])

  const reloadAppliedSearch = useCallback(async () => {
    setRefreshTrigger(prev => prev + 1)
    await loadReviews(appliedFilters)
  }, [appliedFilters, loadReviews])

  useEffect(() => {
    loadReviews(initialFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleToggleResponded = useCallback(
    async (row: ReviewRow) => {
      const nextValue = !row.responded
      setTogglingReviewId(row.id)
      try {
        await api.request(updateReviewResponded(row.id, nextValue))
        await reloadAppliedSearch()
      } catch {
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      } finally {
        setTogglingReviewId(null)
      }
    },
    [reloadAppliedSearch],
  )

  const handleDownloadCsv = async () => {
    setIsDownloadingCsv(true)
    try {
      const response = await api.request(downloadReviewsCsv(currentFilters))
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `reviews-${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    } finally {
      setIsDownloadingCsv(false)
    }
  }

  const columns = useMemo(
    () => [
      { title: 'ID', field: 'id', width: '72px' },
      {
        title: 'Fecha',
        field: 'createdAt',
        render: (row: ReviewRow) =>
          reviewDate(row) ? moment(reviewDate(row)).format(STRINGS.DATE_TIME_FORMAT) : '—',
        customSort: createDateSort<ReviewRow>(row => reviewDate(row)),
      },
      {
        title: 'Local',
        field: 'store.name',
        render: (row: ReviewRow) => row.store?.name ?? '—',
      },
      {
        title: 'Usuario / Reseñador',
        render: (row: ReviewRow) => {
          const label = reviewReviewerLabel(row)
          return label || '—'
        },
        customSort: createStringSort<ReviewRow>(row => reviewReviewerLabel(row)),
      },
      { title: 'Estrellas', field: 'stars' },
      { title: 'Origen', field: 'source' },
      {
        title: 'Resp. Google',
        field: 'googleReplyText',
        render: (row: ReviewRow) => {
          if (row.source !== 'google') return '—'
          return row.googleReplyText ? 'Sí' : 'No'
        },
      },
      {
        title: 'Comentario',
        field: 'description',
        render: (row: ReviewRow) => truncateComment(row.description),
      },
      {
        title: 'WhatsApp',
        field: 'contactWhatsapp',
        render: (row: ReviewRow) => row.contactWhatsapp || '—',
      },
      {
        title: 'Respondido',
        sorting: false,
        render: (row: ReviewRow) => {
          if (row.source !== 'google' && row.replyText) {
            return 'Sí (Plataforma)'
          }
          if (row.source === 'google' && row.googleReplyText) {
            return 'Sí (Google)'
          }
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Switch
                checked={Boolean(row.responded)}
                onChange={() => handleToggleResponded(row)}
                color="primary"
                disabled={!canRespond || togglingReviewId === row.id}
                inputProps={{ 'aria-label': `Marcar review ${row.id} como respondida` }}
              />
              {row.responded ? (
                <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.54)' }}>[Respuesta Manual]</span>
              ) : null}
            </span>
          )
        },
      },
    ],
    [canRespond, handleToggleResponded, togglingReviewId],
  )

  const actions = [
    () => ({
      icon: () => <VisibilityIcon />,
      tooltip: 'Ver comentario completo',
      onClick: (_: React.SyntheticEvent, rowData: ReviewRow | ReviewRow[]) => {
        setSelectedReview(rowData as ReviewRow)
      },
    }),
  ]

  return (
    <div>
      <PageHeader title="Reviews" />
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Desde"
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Hasta"
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Local"
            value={filterStoreId}
            onChange={e => setFilterStoreId(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
          >
            <MenuItem value="">Todos</MenuItem>
            {stores.map(s => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={1}>
          <TextField
            label="Min ★"
            type="number"
            value={filterMinStars}
            onChange={e => setFilterMinStars(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, max: 5 }}
          />
        </Grid>
        <Grid item xs={12} md={1}>
          <TextField
            label="Max ★"
            type="number"
            value={filterMaxStars}
            onChange={e => setFilterMaxStars(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, max: 5 }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            select
            label="Origen"
            value={source}
            onChange={e => setSource(e.target.value)}
            fullWidth
          >
            <MenuItem value="all">Todos</MenuItem>
            {!ConfigUtils.ecard.enabled && <MenuItem value="app">App</MenuItem>}
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="google">Google</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            select
            label="Respondido"
            value={respondedFilter}
            onChange={e => setRespondedFilter(e.target.value as 'all' | 'true' | 'false')}
            fullWidth
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="true">Sí</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={4} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <Button variant="contained" color="primary" onClick={() => handleRefresh()}>
            Actualizar tabla
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleDownloadCsv()}
            disabled={isDownloadingCsv}
          >
            Descargar CSV
          </Button>
        </Grid>
      </Grid>
      <ReviewIndicatorsPanel appliedFilters={appliedFilters} refreshTrigger={refreshTrigger} />
      <DefaultTable
        title=""
        columns={columns}
        data={reviews}
        isLoading={isLoading}
        actions={actions}
        options={{
          showFirstLastPageButtons: true,
          toolbar: true,
        }}
      />
      {selectedReview && (
        <ReviewDetailDialog
          review={selectedReview}
          onClose={() => setSelectedReview(null)}
          onReplied={() => reloadAppliedSearch()}
          canRespond={canRespond}
        />
      )}
    </div>
  )
}

export { Reviews }
